import { useState } from 'react';
import { uploadLeadImages } from '../api/leadImages';

interface UseUploadLeadImagesResult {
  uploadImages: (files: File[]) => Promise<void>;
  isUploading: boolean;
  error: string | null;
}

/**
 * Hook to handle lead image uploads
 * - Manages upload state
 * - Handles errors
 * - Calls success callback when upload completes
 */
export function useUploadLeadImages(
  leadId: number,
  onSuccess?: () => void
): UseUploadLeadImagesResult {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const uploadImages = async (files: File[]) => {
    if (files.length === 0) return;

    setIsUploading(true);
    setError(null);

    try {
      await uploadLeadImages(leadId, files);

      // Call success callback
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      const errorMessage = 'Failed to upload images';
      setError(errorMessage);
      console.error('Error uploading lead images:', err);
      throw new Error(errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  return {
    uploadImages,
    isUploading,
    error,
  };
}
